import React, { useState, useEffect } from 'react'
import HeaderBlack from '../../components/header/headerBlack/headerBlack'
import StatusCard from '../../components/statusCard/statusCard'
import styles from './dossier.module.css'                   
import { useLocation, useNavigate } from 'react-router';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { FaSave } from "react-icons/fa";
import PatientGallery from '../galerie/galerie'
import EternaContent from '../eterna/eterna'
import Correction from '../../components/correction/correction';
import bruillon from "../../assets/img/brouillon.png"
import enplanification from "../../assets/img/en planification.png"
import enattente from "../../assets/img/en attente.png"
import production from "../../assets/img/en production.png"
import termine from "../../assets/img/termine.png"
import enTraitement from "../../assets/img/en traitement.png"
import { onSnapshot } from 'firebase/firestore';

// Helper function to safely format dates
const formatDate = (dateValue) => {
  if (!dateValue) return '-';
  
  try {
    // Handle Firestore timestamp
    if (dateValue && typeof dateValue.toDate === 'function') {
      return new Date(dateValue.toDate()).toLocaleDateString('fr-FR', {
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric'
      });
    }
    
    // Handle regular Date objects or strings
    return new Date(dateValue).toLocaleDateString('fr-FR', {
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric'
    });
  } catch (error) {
    /*console.error('Error formatting date:', error);*/
    return '-';
  }
};

// Add this SVG component at the top of your file
const AnonymousIcon = () => (
    <svg 
        viewBox="0 0 24 24" 
        style={{
            width: '100%',
            height: '100%',
            color: '#718096',  // You can adjust this color
            backgroundColor: '#E2E8F0'  // You can adjust this background color
        }}
        fill="currentColor"
    >
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
    </svg>
);

// Add getStatusImage function
const getStatusImage = (status) => {
  switch (status) {
    case 0:
      return bruillon;
    case 1:
      return enplanification;
    case 2:
      return enattente;
    case 3:
      return production;
    case 4:
      return termine;
    case 5:
      return termine;
  }
};

const Dossier = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [notes, setNotes] = useState('');
    const [isSaving, setSaving] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
    const [showStartTreatmentConfirm, setShowStartTreatmentConfirm] = useState(false);
    const [showEndTreatmentOptions, setShowEndTreatmentOptions] = useState(false);
    const [showCorrection, setShowCorrection] = useState(false);
    const [activeSection, setActiveSection] = useState('progress');
    const [treatmentEnded, setTreatmentEnded] = useState(false);
    
    // Local states for all treatment statuses
    const [localTreatmentStartDate, setLocalTreatmentStartDate] = useState(null);
    const [localAlignersShippedDate, setLocalAlignersShippedDate] = useState(null);
    const [localTpcheckDate, setLocalTpcheckDate] = useState(null);
    const [localPrescriptionDate, setLocalPrescriptionDate] = useState(null);
    const [localTreatmentEndDate, setLocalTreatmentEndDate] = useState(null);
    const days = {
      "Lite": 200,
      "Essential": 340,
      "Smart": 600,
      "Pro": 800,
      "ProPlus": 1100
    }
  
    // Real-time patient data
    const [patientData, setPatientData] = useState(location.state?.patient);

    useEffect(() => {
      if (!patientData?.id) return;
      const unsub = onSnapshot(doc(db, "patients", patientData.id), (docSnap) => {
        if (docSnap.exists()) {
          setPatientData({ ...docSnap.data(), id: docSnap.id });
        }
      });
      return () => unsub();
    }, [patientData?.id]);

    // Initialize notes from patient data
    useEffect(() => {
      if (patientData && patientData.notes) {
        setNotes(patientData.notes);
      }
    }, [patientData]);

    const handleContinuePrescription = () => {
      if (patientData?.status === 0) {
        navigate('/ajouterPatient', { 
          state: { 
            patient: patientData,
            currentStep: 0
          } 
        });
      }
    };

    // Handle notes change
    const handleNotesChange = (e) => {
      setNotes(e.target.value);
    };

    // Save notes to database
    const saveNotes = async () => {
      if (!patientData || notes === patientData.notes) return;
      
      try {
        setSaving(true);
        await updateDoc(doc(db, "patients", patientData.id), {
          notes: notes
        });
        /*console.log("Notes saved successfully");*/
      } catch (error) {
        /*console.error("Error saving notes:", error);*/
      } finally {
        setSaving(false);
      }
    };

    // Archive patient
    const handleArchivePatient = async () => {
      if (!patientData) return;
      
      try {
        setIsArchiving(true);
        await updateDoc(doc(db, "patients", patientData.id), {
          archived: 1,
        });
        setShowArchiveConfirm(false);
        alert("Patient archivé avec succès");
        navigate('/profile');
      } catch (error) {
        /*console.error("Error archiving patient:", error);*/
        alert("Erreur lors de l'archivage du patient");
      } finally {
        setIsArchiving(false);
      }
    };

    // Add handler for starting treatment
    const handleStartTreatment = async () => {
      if (!patientData) return;
      
      try {
        setSaving(true);
        const now = new Date();
        setLocalTreatmentStartDate(now);
        
        await updateDoc(doc(db, "patients", patientData.id), {
          status: 4,
          traitementStartDate: serverTimestamp()
        });
        
        setShowStartTreatmentConfirm(false);
        alert("Traitement commencé avec succès");
      } catch (error) {
        alert("Erreur lors du démarrage du traitement");
      } finally {
        setSaving(false);
      }
    };

    // Add handler for ending treatment
    const handleEndTreatment = async () => {
      if (!patientData) return;
      
      try {
        setSaving(true);
        const now = new Date();
        setLocalTreatmentEndDate(now);
        await updateDoc(doc(db, "patients", patientData.id), {
          status: 5,
          traitementEndDate: serverTimestamp()
        });
        setShowEndTreatmentOptions(false);
        setTreatmentEnded(true);
        alert("Traitement terminé avec succès");
      } catch (error) {
        alert("Erreur lors de la fin du traitement");
      } finally {
        setSaving(false);
      }
    };

    // Add handler for shipping aligners
    const handleShipAligners = async () => {
      if (!patientData) return;
      
      try {
        setSaving(true);
        const now = new Date();
        setLocalAlignersShippedDate(now);
        await updateDoc(doc(db, "patients", patientData.id), {
          alignersShippedDate: serverTimestamp()
        });
        alert("Aligners marqués comme expédiés");
      } catch (error) {
        alert("Erreur lors de la mise à jour du statut");
      } finally {
        setSaving(false);
      }
    };

    // Add handler for TPCheck validation
    const handleTpcheckValidation = async () => {
      if (!patientData) return;
      
      try {
        setSaving(true);
        const now = new Date();
        setLocalTpcheckDate(now);
        await updateDoc(doc(db, "patients", patientData.id), {
          tpcheckDate: serverTimestamp()
        });
        alert("TPCheck validé avec succès");
      } catch (error) {
        alert("Erreur lors de la validation TPCheck");
      } finally {
        setSaving(false);
      }
    };

    // Add handler for prescription submission
    const handlePrescriptionSubmit = async () => {
      if (!patientData) return;
      
      try {
        setSaving(true);
        const now = new Date();
        setLocalPrescriptionDate(now);
        await updateDoc(doc(db, "patients", patientData.id), {
          prescriptionSubmittedAt: serverTimestamp()
        });
        alert("Prescription soumise avec succès");
      } catch (error) {
        alert("Erreur lors de la soumission de la prescription");
      } finally {
        setSaving(false);
      }
    };

    const handleRequestCorrection = () => {
      setShowEndTreatmentOptions(false);
      setTreatmentEnded(true);
      setActiveSection('correction');
    };

    const handleEternaSelection = () => {
      // Only allow eterna selection if it hasn't been commanded yet
      if (patientData?.eternaCommanded) {
        alert("La commande ETERNA a déjà été effectuée pour ce patient");
        return;
      }
      setShowEndTreatmentOptions(false);
      setTreatmentEnded(true);
      setActiveSection('eterna');
    };

    // Replace copy handler function with navigation to simulation
    const handleSimulationNavigation = () => {
      if (!patientData?.TPCheck) {
        alert("Le lien TPCheck n'est pas disponible pour ce patient");
        return;
      }
      
      navigate('/simulation', { 
        state: { 
          patientData: patientData,
          patientId: patientData.id
        } 
      });
    };

    // Add function to calculate expiration date
    const calculateExpirationDate = () => {
      // Use local treatment start date if available, otherwise use patient data
      const startDate = localTreatmentStartDate || 
        (patientData?.traitementStartDate ? 
          (patientData.traitementStartDate.seconds ? 
            new Date(patientData.traitementStartDate.seconds * 1000) : 
            new Date(patientData.traitementStartDate)) : 
          null);
      
      if (!startDate || !patientData?.pack) return '-';
      
      let treatmentDays = 0;
      const packName = patientData.pack;
      
      if (packName.startsWith('Smart')) {
        treatmentDays = days['Smart'];
      } else if (packName.startsWith('Lite')) {
        treatmentDays = days['Lite'];
      } else if (packName.startsWith('Essential')) {
        treatmentDays = days['Essential'];
      } else if (packName === 'Pro') {
        treatmentDays = days['Pro'];
      } else if (packName === 'ProPlus') {
        treatmentDays = days['ProPlus'];
      }
      
      const expirationDate = new Date(startDate);
      expirationDate.setDate(expirationDate.getDate() + treatmentDays);
      
      return expirationDate.toLocaleDateString('fr-FR');
    };

    // If patient data is not found, show message
    if (!patientData) {
      return <div>Patient data not found.</div>;
    }

    /*console.log(patientData);*/

    // Updated to use patientData instead of patient
    const GalleryContent = ({ onBack }) => (
      <PatientGallery onBack={onBack} patient={patientData} />
    );

    // Add your progress content
    const ProgressContent = () => (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <h3>Avancement de la commande</h3>
          {patientData?.status === 0 && (
            <button 
              className={`${styles.btnContinue}`} 
              onClick={handleContinuePrescription}
            >
              Continuer la prescription
            </button>
          )}
        </div>
        {patientData?.status === 0 ? (
          <>
            <img className={styles.progressiveBar} src={getStatusImage(patientData.status)} alt="status bar" />
            <div className={styles.comprehensivePackage}>
              <h3>{patientData.pack}</h3>
              <table>
                <thead>
                  <tr>
                  </tr>
                </thead>
                <tbody>
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            <div className={styles.buttonsContainer}>
              <button 
                className={`${styles.btnGallery} ${styles.galerieButton}`}
                onClick={() => setActiveSection('gallery')}
              >
                <div className={styles.gridIcon}>
                  <div className={styles.gridSquare}></div>
                  <div className={styles.gridSquare}></div>
                  <div className={styles.gridSquare}></div>
                  <div className={styles.gridSquare}></div>
                </div>
                <span className={styles.buttonText}>Galerie</span>
              </button>
              <button 
                className={styles.btnTpCheck}
                onClick={() => navigate('/tpcheck', { 
                  state: { 
                    patientData: patientData,
                    patientId: patientData.id
                  } 
                })}
              >
                TPCheck
              </button>
             
            </div>
            {patientData?.status === 5 && (
              <div className={styles.eternaButtonContainer}>
                <button 
                  className={styles.btnEterna}
                  onClick={() => setActiveSection('eterna')}
                  disabled={patientData?.eternaCommanded}
                  style={patientData?.eternaCommanded ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                >
                  Commander la contention ETERNA®
                </button>
              </div>
            )}
            <img className={styles.progressiveBar} 
              src={(patientData?.alignersShippedDate || localAlignersShippedDate) ? enTraitement : getStatusImage(patientData.status)} 
              alt="status bar" />
            <div className={styles.comprehensivePackage}>
              <h3>{patientData.pack}</h3>
              <table>
                <thead>
                  <tr>
                  </tr>
                </thead>
                <tbody>
                  {/* Treatment End Row */}
                  {(patientData?.traitementEndDate || patientData?.eternaCommandDate || localTreatmentEndDate) && (
                    <tr>
                      <td>{localTreatmentEndDate ? 
                        localTreatmentEndDate.toLocaleDateString('fr-FR') :
                        (patientData.traitementEndDate?.seconds ? 
                          new Date(patientData.traitementEndDate.seconds * 1000).toLocaleDateString('fr-FR') : 
                          patientData.eternaCommandDate?.seconds ?
                            new Date(patientData.eternaCommandDate.seconds * 1000).toLocaleDateString('fr-FR') :
                            new Date().toLocaleDateString('fr-FR'))}</td>
                      <td>
                        <span>Traitement terminé</span>
                      </td>
                    </tr>
                  )}

                  {/* Treatment Start Row */}
                  {(patientData?.traitementStartDate || localTreatmentStartDate) && (
                    <tr>
                      <td>{localTreatmentStartDate ? 
                        localTreatmentStartDate.toLocaleDateString('fr-FR') :
                        (patientData.traitementStartDate.seconds ? 
                          new Date(patientData.traitementStartDate.seconds * 1000).toLocaleDateString('fr-FR') : 
                          new Date().toLocaleDateString('fr-FR'))}</td>
                      <td>
                        <span>Traitement commencé</span>
                        {!patientData?.traitementEndDate && !localTreatmentEndDate && (
                          <button 
                            className={`${styles.btnStatus} ${styles.check}`}
                            onClick={() => setShowEndTreatmentOptions(true)}
                          >
                            Terminer le traitement
                          </button>
                        )}
                      </td>
                    </tr>
                  )}

                  {/* Aligners Shipped Row */}
                  {(patientData?.alignersShippedDate || localAlignersShippedDate) && (
                    <tr>
                      <td>{localAlignersShippedDate ? 
                        localAlignersShippedDate.toLocaleDateString('fr-FR') :
                        (patientData.alignersShippedDate.seconds ? 
                          new Date(patientData.alignersShippedDate.seconds * 1000).toLocaleDateString('fr-FR') : 
                          new Date().toLocaleDateString('fr-FR'))}</td>
                      <td>
                        <span>Aligners expédiés</span>
                        {!patientData?.traitementStartDate && !localTreatmentStartDate && (
                          <button 
                            className={`${styles.btnStatus} ${styles.check}`}
                            onClick={() => setShowStartTreatmentConfirm(true)}
                          >
                            Commencer le traitement
                          </button>
                        )}
                      </td>
                    </tr>
                  )}

                  {/* TPCheck Row */}
                  {(patientData?.tpcheckDate || localTpcheckDate) && (
                    <tr>
                      <td>{localTpcheckDate ? 
                        localTpcheckDate.toLocaleDateString('fr-FR') :
                        (patientData.tpcheckDate.seconds ? 
                          new Date(patientData.tpcheckDate.seconds * 1000).toLocaleDateString('fr-FR') : 
                          new Date().toLocaleDateString('fr-FR'))}</td>
                      <td>
                        <span>Plan de traitement TPCheck disponible</span>
                      </td>
                    </tr>
                  )}

                  {/* Prescription Row */}
                  {((patientData?.prescriptionSubmittedAt || localPrescriptionDate) || 
                    (patientData?.status >= 1 && patientData?.createdAt)) && (
                    <tr>
                      <td>
                        {localPrescriptionDate ? 
                          localPrescriptionDate.toLocaleDateString('fr-FR') :
                          patientData?.prescriptionSubmittedAt ? 
                            (patientData.prescriptionSubmittedAt.seconds ? 
                              new Date(patientData.prescriptionSubmittedAt.seconds * 1000).toLocaleDateString('fr-FR') : 
                              new Date().toLocaleDateString('fr-FR')) :
                          patientData?.createdAt ?
                            (patientData.createdAt.seconds ? 
                              new Date(patientData.createdAt.seconds * 1000).toLocaleDateString('fr-FR') : 
                              new Date().toLocaleDateString('fr-FR')) :
                          new Date().toLocaleDateString('fr-FR')}
                      </td>
                      <td>
                        <span>Prescription soumise</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </>
    );

    return (
      <div>
        <HeaderBlack />
        <main>
          <StatusCard />
          <div className={styles.patientCard}>
            {/* Card Header */}
            <div className={styles.cardHeader}>
              <h2 className={styles.title}>Dossier patient</h2>
              <button className={styles.closeBtn} onClick={() => navigate('/profile')}>✕</button>
            </div>

            {/* Patient Section */}
            <div className={styles.patientSection}>
              {/* Patient Info */}
              <div className={styles.patientInfo}>
                <div className={styles.patientProfile}>
                  {patientData.images?.img2 ? (
                    <img
                      src={patientData.images.img2}
                      alt="Patient img"
                      className={styles.patientPhoto}
                    />
                  ) : (
                    <div className={styles.defaultProfileIcon}>
                      <AnonymousIcon />
                    </div>
                  )}
                  <div className={styles.profileActions}>
                    <button 
                      className={styles.btnArchiver} 
                      onClick={() => setShowArchiveConfirm(true)}
                      disabled={isArchiving}
                    >
                      {isArchiving ? 'Archivage...' : 'Archiver'}
                    </button>
                  </div>
                </div>
                <div className={styles.patientDetails}>
                  <h3>{`${patientData.name} ${patientData.surname || ''}`}</h3>
                  <p>Né(e) le : {patientData.dateOfBirth || 'Non spécifié'}</p>
                  <p>Patient : #{patientData.id.slice(0, 8)}</p>

                  {/* Notes Section */}
                  <div className={styles.notesSection}>
                    <h4>Notes</h4>
                    <div className={styles.textareaContainer}>
                      <textarea 
                        placeholder="Aucun" 
                        value={notes}
                        onChange={handleNotesChange}
                        onBlur={saveNotes}
                      />
                      <div 
                        className={styles.saveIcon} 
                        onClick={saveNotes}
                        title="Enregistrer"
                      >
                        <FaSave />
                      </div>
                    </div>
                    {isSaving && <span className={styles.savingIndicator}>Enregistrement...</span>}
                  </div>
                  
                  <p>Type de patient : {patientData.category}</p>
                  <p>Pack : {patientData.pack || "-"}</p>
                  <p>Nombre d'aligneurs supérieur : {patientData.upperArcadeSteps !== undefined ? patientData.upperArcadeSteps : '-'}</p>
                  <p>Nombre d'aligneurs inférieur : {patientData.lowerArcadeSteps !== undefined ? patientData.lowerArcadeSteps : '-'}</p>
                  <p>Rythme de changement : {patientData?.changeRhythm || "-" } jours</p>
                  <p>Devis: {patientData?.price || "-" } dt HT</p>
                  <p>Date d'expiration du traitement: {calculateExpirationDate()}</p>
                </div>
              </div>

              {/* Treatment Progress */}
              <div className={styles.treatmentProgress}>
                {/* Only show buttons when not in gallery view */}
                {activeSection === 'progress' && (
                  <>
                    {patientData?.status !== 0 && (
                      <div className={styles.buttonsContainer}>
                        <button 
                          className={`${styles.btnGallery} ${styles.galerieButton}`}
                          onClick={() => setActiveSection('gallery')}
                        >
                          <div className={styles.gridIcon}>
                            <div className={styles.gridSquare}></div>
                            <div className={styles.gridSquare}></div>
                            <div className={styles.gridSquare}></div>
                            <div className={styles.gridSquare}></div>
                          </div>
                          <span className={styles.buttonText}>Galerie</span>
                        </button>
                        <button 
                          className={styles.btnTpCheck}
                          onClick={() => navigate('/tpcheck', { 
                            state: { 
                              patientData: patientData,
                              patientId: patientData.id
                            } 
                          })}
                        >
                          TPCheck
                        </button>
                      
                        <button 
                          className={`${styles.btnTpCheck} ${styles.simulationButton}`}
                          onClick={handleSimulationNavigation}
                        >
                          Simulation Patient
                        </button>
                      </div>
                    )}
                    <ProgressContent />
                  </>
                )}

                {/* Conditional rendering for gallery, eterna and correction */}
                {activeSection === 'gallery' && (
                  <GalleryContent onBack={() => setActiveSection('progress')} />
                )}
                {activeSection === 'eterna' && (
                  <EternaContent onBack={() => setActiveSection('progress')} patient={patientData} />
                )}
                {activeSection === 'correction' && (
                  <Correction 
                    patient={patientData} 
                    onBack={() => setActiveSection('progress')} 
                  />
                )}
              </div>
            </div>
          </div>
        </main>

        {/* Archive Confirmation Popup */}
        {showArchiveConfirm && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
              <h3>Êtes-vous sûr de vouloir archiver ce patient ?</h3>
              <div className={styles.modalButtons}>
                <button onClick={handleArchivePatient} className={`${styles.modalButton} ${styles.confirmButton}`}>Oui</button>
                <button onClick={() => setShowArchiveConfirm(false)} className={`${styles.modalButton} ${styles.cancelButton}`}>Non</button>
              </div>
            </div>
          </div>
        )}

        {/* Start Treatment Confirmation Popup */}
        {showStartTreatmentConfirm && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
              <button 
                className={styles.popupCloseBtn}
                onClick={() => setShowStartTreatmentConfirm(false)}
              >
                ✕
              </button>
              <h3>Êtes-vous sûr de vouloir commencer le traitement ?</h3>
              <div className={styles.modalButtons}>
                <button onClick={handleStartTreatment} className={`${styles.modalButton} ${styles.confirmButton}`}>Oui</button>
                <button onClick={() => setShowStartTreatmentConfirm(false)} className={`${styles.modalButton} ${styles.cancelButton}`}>Non</button>
              </div>
            </div>
          </div>
        )}

        {/* End Treatment Options Popup */}
        {showEndTreatmentOptions && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
              <button 
                className={styles.popupCloseBtn}
                onClick={() => setShowEndTreatmentOptions(false)}
              >
                ✕
              </button>
              <h3>Que souhaitez-vous faire ?</h3>
              <div className={styles.modalButtons}>
                <button 
                  onClick={handleEternaSelection}
                  className={`${styles.modalButton} ${styles.eternaButton}`}
                  disabled={patientData?.eternaCommanded}
                  style={patientData?.eternaCommanded ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                >
                  {patientData?.eternaCommanded ? 'Commande ETERNA déjà effectuée' : 'Commander la contention ETERNA'}
                </button>
                <button 
                  onClick={handleEndTreatment}
                  className={`${styles.modalButton} ${styles.endButton}`}
                >
                  Terminer le traitement
                </button>
                <button 
                  onClick={handleRequestCorrection}
                  className={`${styles.modalButton} ${styles.correctionButton}`}
                >
                  Demander une correction
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
};

export default Dossier;